import { computed, inject, Injectable, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { catchError, Observable, tap, throwError } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthStore } from '@domains/auth/auth.store';
import { TagApi } from './tag.api';
import { EMPTY_TAGS, ITag, ITagApi } from './tag.model';

const PAGE_SIZE = 10;

@Injectable({ providedIn: 'root' })
export class TagStore {
  private readonly api = inject(TagApi);
  private readonly authStore = inject(AuthStore);

  readonly pageSize = PAGE_SIZE;

  readonly page = signal(0);

  private readonly _tags = rxResource({
    params: () => ({
      page: this.page() + 1,
      limit: PAGE_SIZE,
      ordering: '-id',
    }),
    stream: ({ params }) => this.api.getTags(params),
  });

  readonly tags = computed<ITag[]>(() =>
    (this._tags.value()?.results ?? EMPTY_TAGS).map(mapTag),
  );
  readonly total = computed(() => this._tags.value()?.count ?? 0);
  readonly isLoading = computed(() => this._tags.isLoading());
  readonly error = computed(() => this._tags.error());

  setPage(page: number): void {
    this.page.set(page);
  }

  createTag(name: string): Observable<ITagApi> {
    const organization = this.authStore.currentUser()?.organization.id;
    if (!organization) {
      return throwError(() => new Error('No authenticated organization'));
    }
    return this.api.createTag({ name, organization }).pipe(tap(() => this._tags.reload()));
  }

  /**
   * Try a partial PATCH first; if the API rejects it (405/400), fall back to a
   * full PUT carrying the entire tag object.
   */
  updateTag(tag: ITag, name: string): Observable<ITagApi> {
    return this.api.patchTag(tag.id, name).pipe(
      catchError((err: unknown) => {
        if (err instanceof HttpErrorResponse && (err.status === 405 || err.status === 400)) {
          return this.api.putTag({ ...tag, name });
        }
        return throwError(() => err);
      }),
      tap(() => this._tags.reload()),
    );
  }

  deleteTag(id: number): Observable<void> {
    return this.api.deleteTag(id).pipe(tap(() => this._tags.reload()));
  }
}

/** Collapse the (currently expanded) `organization` field down to its id. */
function mapTag(raw: ITagApi): ITag {
  return {
    id: raw.id,
    name: raw.name,
    organization: typeof raw.organization === 'number' ? raw.organization : raw.organization.id,
  };
}
